import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, FileText, Shield, Upload } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { apiService } from '../../services/api';
import { VendorCoiPublicPolicy, VendorCoiPublicUpload } from '../../types';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
};

const toDateInputValue = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

interface PolicyUploadState {
  carrierName: string;
  policyNumber: string;
  expirationDate: string;
  file: File | null;
  isUploading: boolean;
  uploaded: boolean;
}

const VendorCoiUploadPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [details, setDetails] = useState<VendorCoiPublicUpload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [policyState, setPolicyState] = useState<Record<number, PolicyUploadState>>({});

  useEffect(() => {
    const loadDetails = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await apiService.getPublicVendorCoiUpload(token);
        if (response.success && response.data) {
          setDetails(response.data);
          const initial: Record<number, PolicyUploadState> = {};
          response.data.policies.forEach((policy) => {
            initial[policy.policyId] = {
              carrierName: policy.carrierName ?? '',
              policyNumber: policy.policyNumber ?? '',
              expirationDate: toDateInputValue(policy.expirationDate),
              file: null,
              isUploading: false,
              uploaded: false,
            };
          });
          setPolicyState(initial);
        } else {
          toast.error(response.message || 'Invalid upload link');
        }
      } catch {
        toast.error('Unable to load upload details');
      } finally {
        setIsLoading(false);
      }
    };

    loadDetails();
  }, [token]);

  const updatePolicyState = (policyId: number, patch: Partial<PolicyUploadState>) => {
    setPolicyState((prev) => ({
      ...prev,
      [policyId]: { ...prev[policyId], ...patch },
    }));
  };

  const handleUpload = async (policy: VendorCoiPublicPolicy) => {
    const state = policyState[policy.policyId];
    if (!state?.file) {
      toast.error('Choose a COI file to upload');
      return;
    }

    updatePolicyState(policy.policyId, { isUploading: true });
    try {
      const response = await apiService.uploadPublicVendorCoi(token, policy.policyId, state.file, {
        expirationDate: state.expirationDate || undefined,
        carrierName: state.carrierName || undefined,
        policyNumber: state.policyNumber || undefined,
      });

      if (response.success) {
        toast.success(response.data?.message || response.message || 'COI uploaded');
        updatePolicyState(policy.policyId, { uploaded: true, file: null, isUploading: false });
        setDetails((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            policies: prev.policies.map((p) =>
              p.policyId === policy.policyId
                ? {
                    ...p,
                    hasDocument: true,
                    isVerified: false,
                    carrierName: state.carrierName || p.carrierName,
                    policyNumber: state.policyNumber || p.policyNumber,
                    expirationDate: state.expirationDate
                      ? new Date(state.expirationDate).toISOString()
                      : p.expirationDate,
                  }
                : p
            ),
          };
        });
      } else {
        toast.error(response.message || 'Unable to upload COI');
        updatePolicyState(policy.policyId, { isUploading: false });
      }
    } catch {
      toast.error('Unable to upload COI');
      updatePolicyState(policy.policyId, { isUploading: false });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!token || !details) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-lg w-full">
          <CardContent className="py-10 text-center space-y-3">
            <Shield className="h-10 w-10 mx-auto text-red-500" />
            <h1 className="text-xl font-semibold text-gray-900">Invalid upload link</h1>
            <p className="text-sm text-gray-600">
              This link is missing, invalid, or has expired. Contact the company that requested your Certificate of Insurance for a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (details.isExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-lg w-full">
          <CardContent className="py-10 text-center space-y-3">
            <Shield className="h-10 w-10 mx-auto text-amber-500" />
            <h1 className="text-xl font-semibold text-gray-900">Link expired</h1>
            <p className="text-sm text-gray-600">
              This upload link expired on {formatDate(details.expiresAt)}. Ask {details.companyName} to send a new link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 text-blue-700">
            <FileText className="h-6 w-6" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Upload Certificate of Insurance</h1>
          <p className="text-gray-600">
            <strong>{details.companyName}</strong> is requesting updated COI documents for{' '}
            <strong>{details.vendorName}</strong>.
          </p>
          {details.requirePolicyVerification ? (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-block">
              Uploaded documents will be marked pending until {details.companyName} verifies them.
            </p>
          ) : null}
        </div>

        {details.policies.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-600">
              No insurance policies are on file yet. Contact {details.companyName} to add the required policy types before uploading.
            </CardContent>
          </Card>
        ) : (
          details.policies.map((policy) => {
            const state = policyState[policy.policyId];
            return (
              <Card key={policy.policyId}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between gap-3 text-lg">
                    <span>{policy.policyTypeLabel}</span>
                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                      {state?.uploaded || policy.hasDocument
                        ? policy.isVerified
                          ? 'On file / verified'
                          : 'On file / pending review'
                        : policy.policyStatus}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(state?.uploaded || policy.hasDocument) && (
                    <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                      <CheckCircle className="h-4 w-4" />
                      {state?.uploaded
                        ? 'Upload received. You can replace the file if needed.'
                        : 'A document is already on file. Uploading will replace it.'}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor={`carrier-${policy.policyId}`}>Carrier name</Label>
                      <Input
                        id={`carrier-${policy.policyId}`}
                        value={state?.carrierName ?? ''}
                        onChange={(e) => updatePolicyState(policy.policyId, { carrierName: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor={`number-${policy.policyId}`}>Policy number</Label>
                      <Input
                        id={`number-${policy.policyId}`}
                        value={state?.policyNumber ?? ''}
                        onChange={(e) => updatePolicyState(policy.policyId, { policyNumber: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor={`exp-${policy.policyId}`}>Expiration date</Label>
                      <Input
                        id={`exp-${policy.policyId}`}
                        type="date"
                        value={state?.expirationDate ?? ''}
                        onChange={(e) => updatePolicyState(policy.policyId, { expirationDate: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor={`file-${policy.policyId}`}>COI document (PDF or image)</Label>
                      <Input
                        id={`file-${policy.policyId}`}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) =>
                          updatePolicyState(policy.policyId, { file: e.target.files?.[0] ?? null })
                        }
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      type="button"
                      onClick={() => handleUpload(policy)}
                      disabled={state?.isUploading || !state?.file}
                      aria-label={`Upload COI for ${policy.policyTypeLabel}`}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {state?.isUploading ? 'Uploading...' : 'Upload COI'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}

        <p className="text-center text-xs text-gray-500">
          This secure link expires on {formatDate(details.expiresAt)}. No login is required.
        </p>
      </div>
    </div>
  );
};

export default VendorCoiUploadPage;
